import { PromotionIntentInput } from '../types/promotion'
import { addCommaToPrice } from './numbers'

export const MAX_PROMOTION_CODE_LENGTH = 128

/**
 * TRANSPORT failures only. Not business outcomes.
 *
 * This union used to carry four codes and four English sentences covering every way a
 * promotion could fail. That was a client-side reason-to-copy table standing in for one
 * the promotion service already has: it distinguishes roughly eighteen reasons and
 * translates each into FA and EN. Collapsed to four, "the promotion code is expired or
 * not eligible for this stay" meant a wrong property, a blackout date, a day-of-week
 * rule, an advance-purchase window, an exhausted cap and an exhausted budget alike — and
 * a booker cannot act on any of them.
 *
 * The three business codes are gone. Those answers now come from evaluatePromotion as
 * `reasonText`, rendered verbatim, per the contract's own instruction that consumers MUST
 * NOT re-derive copy from the enum.
 *
 * PROMOTION_UNAVAILABLE stays because it is genuinely ours to word: it means nothing was
 * evaluated, so there is no service sentence to show. It maps from gRPC Unavailable and
 * DeadlineExceeded — the only two codes the BFF's own mapper considers safe to attribute
 * to the service being down.
 */
export type PromotionErrorCode =
  | 'PROMOTION_UNAVAILABLE'
  | 'PROMOTION_CODE_TOO_LONG'

const promotionErrorMessages: Record<PromotionErrorCode, string> = {
  PROMOTION_UNAVAILABLE:
    'Promotion evaluation is temporarily unavailable. Try again or continue without a code.',
  // OURS to word, because it is our check: buildPromotionIntent refuses an over-length
  // code before any request is made, so no service ever sees it and there is no
  // reasonText to show. Near-unreachable in practice — the field sets the same maxLength —
  // which is exactly why it needs a message of its own rather than borrowing a service
  // code that would send a reader looking for a rejection that never happened.
  PROMOTION_CODE_TOO_LONG: 'The promotion code is too long.'
}

export function promotionErrorMessage(code: PromotionErrorCode): string {
  return promotionErrorMessages[code]
}

export function buildPromotionIntent(
  rawCode: string
): PromotionIntentInput | undefined {
  if (rawCode.length > MAX_PROMOTION_CODE_LENGTH) {
    return undefined
  }

  const code = rawCode.trim()
  return {
    applyAutomatic: true,
    ...(code ? { code } : {})
  }
}

const currencyMinorDigits: Record<string, number> = {
  IRR: 0,
  USD: 2,
  EUR: 2,
  GBP: 2,
  AED: 2,
  TRY: 2,
  CNY: 2,
  RUB: 2,
  IQD: 3
}

export function formatPromotionMinorAmount(
  minorAmount: string,
  currency: string
): string {
  if (!/^\d+$/.test(minorAmount)) {
    return minorAmount
  }

  const fractionDigits = currencyMinorDigits[currency] ?? 0
  if (fractionDigits === 0) {
    return String(addCommaToPrice(minorAmount))
  }

  const padded = minorAmount.padStart(fractionDigits + 1, '0')
  const whole = padded.slice(0, -fractionDigits)
  const fraction = padded.slice(-fractionDigits)
  return `${addCommaToPrice(whole)}.${fraction}`
}

/**
 * The error `code` an Apollo failure carries, wherever it happens to be.
 *
 * Both shapes are checked because both occur: `graphQLErrors` on a response that reached
 * us, `cause` on a link-level failure. Factored out because the two callers below derived
 * it identically, and a change to the extensions shape had to be made twice or not at all.
 *
 * Returns undefined for anything that is not an object carrying a string code — including
 * null, a bare string, and a thrown non-error — so callers do not each repeat that guard.
 */
function errorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined
  }

  const candidate = error as {
    graphQLErrors?: Array<{ extensions?: Record<string, unknown> }>
    cause?: { extensions?: Record<string, unknown> }
  }
  const code =
    candidate.graphQLErrors?.[0]?.extensions?.code ??
    candidate.cause?.extensions?.code

  return typeof code === 'string' ? code : undefined
}

export function extractPromotionErrorCode(
  error: unknown
): PromotionErrorCode | undefined {
  switch (errorCode(error)) {
    // Only the outage case. A business refusal reaching this path at all means the
    // booking was attempted without the code having been applied first, and it is
    // deliberately NOT translated into one of our own sentences here — the evaluate
    // query is where a refusal gets its wording.
    case 'PROMOTION_UNAVAILABLE':
      return 'PROMOTION_UNAVAILABLE'
    default:
      return undefined
  }
}

/**
 * Whether a failed booking failed BECAUSE OF the promotion.
 *
 * Separate from extractPromotionErrorCode on purpose. That function answers "is this a
 * transport failure I have my own wording for", and it deliberately stopped recognising
 * the three business codes — a refusal should arrive from evaluatePromotion carrying the
 * service's translated sentence, not be re-worded here.
 *
 * This answers a different question: the booking failed, was the promotion the cause? The
 * booker can still reach that state by typing a code and pressing Continue without
 * pressing Apply, and without this they would be shown the raw English GraphQL message.
 * It reports only WHETHER, never why; the why belongs to Apply.
 */
export function isPromotionRejection(error: unknown): boolean {
  return errorCode(error)?.startsWith('PROMOTION_') ?? false
}

/**
 * What a failed primitive reservation actually was.
 *
 * The handler that consumed this was four `if`s inside a mutation's onError, in a screen
 * with no test file — so the ORDER of those branches, which is the only thing that can go
 * wrong here, was verified by reading. The branches themselves are obvious; the ordering
 * is not, and it is where the damage is:
 *
 * `isPromotionRejection` matches on the `PROMOTION_` prefix and sits ahead of the
 * cart-expired check. Widen it and it swallows the expired cart, so a booker whose cart
 * timed out is told their discount failed and never sees the dialog that clears the stale
 * cart and lets them start again. Their booking becomes unrecoverable without a manual
 * refresh they have no reason to try.
 *
 * Extracted so that ordering can be tested without mounting a screen that pulls in the
 * router, the client and half a dozen queries. The handler keeps the side effects — toasts,
 * modal, storage — and this decides only which of them applies.
 */
export type PrimitiveFailure =
  | { kind: 'transport'; code: PromotionErrorCode }
  | { kind: 'promotion' }
  | { kind: 'cartExpired' }
  | { kind: 'unknown' }

export function classifyPrimitiveFailure(
  error: unknown,
  isCartExpired: (error: unknown) => boolean
): PrimitiveFailure {
  // Transport first: it is the only kind with wording of our own, and the only one where
  // nothing was evaluated at all.
  const code = extractPromotionErrorCode(error)
  if (code) {
    return { kind: 'transport', code }
  }
  // Then a promotion refusal, which needs words but not ours specifically.
  if (isPromotionRejection(error)) {
    return { kind: 'promotion' }
  }
  // Then the expired cart, which needs a dialog and cleanup rather than a message.
  if (isCartExpired(error)) {
    return { kind: 'cartExpired' }
  }
  return { kind: 'unknown' }
}
